import React, { useCallback, useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";

import Svg from "../../../../../components/svg/Svg";
import { Info } from "../../../../../components/svg/svgImageList";
import Button from "../../../../../components/button/Button";
import useTheme from "../../../../../services/theme-service/ThemeService";
import "./Legend.scss";
import { translationService } from "../../../../../services/translation/TranslationService";
import { permissionsService } from "../../../../../services/authorization/UserPermissionsService";
import {
  CreateDamage,
  CreateDamageInAssignedProject,
} from "../../../../../services/authorization/Permissions";
import CodeDetectionButton from "../../../../../components/buttons/code-detection-button/CodeDetectionButton";
import { AnalysisService } from "../../../../../services/analysis/AnalysisService";
import { CodeDetection } from "../../../../../api-models/response/VideoAnalysisDataResponse";

interface LegendProps {
  videoAnalysisService: AnalysisService;
  currentInspectionIsLocked: boolean;
}

enum LegendTabs {
  Overview,
  Characterization,
}

export default observer(function Legend(props: LegendProps) {
  const { videoAnalysisService, currentInspectionIsLocked } = props;
  const { themeClass } = useTheme();

  const [tabSelection, setTabSelection] = useState<LegendTabs>(LegendTabs.Overview);
  const isOverviewSelected = useMemo(() => tabSelection === LegendTabs.Overview, [tabSelection]);
  const isCharacterizationSelected = useMemo(
    () => tabSelection === LegendTabs.Characterization,
    [tabSelection]
  );

  const handleOverviewClick = useCallback(() => {
    setTabSelection(LegendTabs.Overview);
    videoAnalysisService.changeView(true);
  }, [videoAnalysisService]);

  const handleCharacterizationsClick = useCallback(() => {
    setTabSelection(LegendTabs.Characterization);
    videoAnalysisService.changeView(false);
  }, [videoAnalysisService]);

  const handleMainCodeClick = useCallback(
    (codeDetection: CodeDetection) => {
      if (!codeDetection.isDetected) return;

      videoAnalysisService.changeMainCodeVisibility(codeDetection.code);
      videoAnalysisService.toggleMainCode(codeDetection.code, !codeDetection.isVisible!);
    },
    [videoAnalysisService]
  );

  useEffect(() => {
    videoAnalysisService.loadMainCodesDetected();
    videoAnalysisService.loadCharacterizationsDetected();
  }, [videoAnalysisService]);

  return (
    <div className="legend-container">
      <h3 className="legend-section">{translationService.translate("Analysis.legend")}</h3>

      <div className="legend-section">
        <div className="buttons-container">
          <Button
            textKey="general.overview"
            onClick={handleOverviewClick}
            className={themeClass(["btn-big", isOverviewSelected ? "" : "fill-color-ghost"])}
          />
          <Button
            textKey="Analysis.legend-characterizations"
            onClick={handleCharacterizationsClick}
            className={themeClass([
              "btn-big",
              isCharacterizationSelected ? "" : "fill-color-ghost",
            ])}
          />
        </div>
      </div>

      <div className="legend-section detections-with-color-container">
        {videoAnalysisService.detectedMainCodes.map((mainCode: CodeDetection) => {
          return (
            <CodeDetectionButton
              key={mainCode.code}
              codeDetection={mainCode}
              handleClick={handleMainCodeClick}
            />
          );
        })}
      </div>

      {isCharacterizationSelected && (
        <div className="legend-section detections-with-color-container">
          {videoAnalysisService.detectedCharacterizations.map((characterizationCode) => {
            return (
              <div
                className="detection-color-code"
                key={characterizationCode.code + characterizationCode.colorCode}
                style={{ backgroundColor: characterizationCode.colorCode }}
              >
                {characterizationCode.code}
              </div>
            );
          })}
        </div>
      )}

      {isOverviewSelected && (
        <div className="legend-section">
          <div className="pauses disabled">
            {translationService.translate("Analysis.legend-pauses")}
          </div>
        </div>
      )}

      {permissionsService.userHasOneOfThePermissions([
        CreateDamage,
        CreateDamageInAssignedProject,
      ]) &&
        !currentInspectionIsLocked && (
          <div className="legend-section">
            <Svg className="svg-info" svg={Info} />
            <legend>{translationService.translate("Analysis.legend-add-new-damage")}</legend>
          </div>
        )}
    </div>
  );
});
